import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Coins, Sparkles, Zap } from "lucide-react";

const NEON = {
  starter: "#8A8A93",
  small: "#00F0FF",
  medium: "#CCFF00",
  large: "#FF5900",
  epic: "#FF1053",
  ultimate: "#F59E0B",
};
const LABELS = {
  starter: "Starter",
  small: "Small",
  medium: "Bump",
  large: "Fat Stack",
  epic: "Whale",
  ultimate: "Ultimate",
};

export default function Credits() {
  const { user, refreshMe } = useAuth();
  const nav = useNavigate();
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/credits/packs").then(r => setPacks(r.data));
    refreshMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buy = async (packId) => {
    if (!user) return nav("/login");
    setBusy(packId);
    try {
      const r = await api.post("/credits/checkout", { pack_id: packId, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to start checkout");
      setBusy("");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
      <div className="text-center mb-12">
        <div className="sc-label" style={{ color: "#FFD700" }}>Buy Gold Coins</div>
        <h1 className="sc-heading text-5xl sm:text-6xl font-black mt-2 tracking-tight" style={{ background: "linear-gradient(135deg,#FFD700,#F59E0B,#FFD700)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Gold Coins</h1>
        <p className="text-sm text-zinc-400 max-w-xl mx-auto mt-4">
          Gold Coins are used to <span className="text-white font-semibold">tip streamers</span> and <span className="text-white font-semibold">subscribe</span>.
          <span className="block mt-1">Every <span className="sc-mono text-white">$1</span> spent also mints <span className="text-[color:var(--sc-cyan)] font-bold">+10 Sparks</span> for the Bling Shop.</span>
        </p>
        {user && (
          <div className="inline-flex items-center gap-2 mt-6 flex-wrap justify-center">
            <div className="gold-pill" data-testid="credits-gold-balance">
              <Coins className="w-4 h-4" /> {(user.gold_coins ?? 0).toLocaleString()} <span className="text-[10px] tracking-widest uppercase text-zinc-500 ml-1">Gold Coins</span>
            </div>
            <div className="sparks-pill" data-testid="credits-sparks-balance">
              <Sparkles className="w-3.5 h-3.5" /> {(user.sparks ?? 0).toLocaleString()} <span className="text-[10px] tracking-widest uppercase text-zinc-500 ml-1">Sparks</span>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
        {packs.map((p) => {
          const color = NEON[p.id] || "#FF1053";
          const total = p.total ?? (p.credits + (p.bonus || 0));
          const isBest = p.id === "ultimate";
          return (
            <div
              key={p.id}
              data-testid={`credit-pack-${p.id}`}
              className={`sc-card p-6 relative overflow-hidden ${isBest ? "sc-neon-volt" : ""}`}
              style={{ borderColor: isBest ? undefined : color + "40" }}
            >
              {isBest && (
                <div className="absolute -top-1 -right-1 px-3 py-1 sc-heading font-black text-[10px] tracking-widest bg-[color:var(--sc-volt)] text-black rounded-bl-md rounded-tr-md flex items-center gap-1">
                  <Zap className="w-3 h-3" /> BEST VALUE
                </div>
              )}
              <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full blur-3xl opacity-20" style={{ background: color }} />

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <div className="sc-label sc-mono" style={{ color }}>{LABELS[p.id] || p.id}</div>
                  <Coins className="w-5 h-5" style={{ color: "#FFD700" }} />
                </div>

                <div className="sc-heading text-5xl font-black mt-2 tracking-tight" style={{ color: "#FFD700" }}>
                  {total.toLocaleString()}
                </div>
                <div className="text-xs sc-label mt-1" style={{ color: "#FFD700" }}>Gold Coins</div>

                {p.bonus > 0 && (
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="sc-mono text-xs text-zinc-500">{p.credits.toLocaleString()} base</span>
                    <span className="sc-mono text-xs font-bold" style={{ color: "var(--sc-volt)" }}>
                      + {p.bonus.toLocaleString()} BONUS COINS
                    </span>
                  </div>
                )}
                {p.bonus === 0 && (
                  <div className="mt-2 sc-mono text-xs text-zinc-500">Base pack</div>
                )}

                {/* Sparks bonus */}
                {p.bonus_sparks > 0 && (
                  <div className="mt-3 flex items-center gap-1.5 p-2 border border-[color:var(--sc-cyan)]/30 bg-[color:var(--sc-cyan)]/5 rounded-sm" data-testid={`credit-pack-${p.id}-bonus-sparks`}>
                    <span className="text-[color:var(--sc-cyan)] font-bold">+{p.bonus_sparks.toLocaleString()}</span>
                    <span className="text-[10px] sc-mono tracking-widest uppercase text-zinc-400">Bonus Sparks</span>
                  </div>
                )}

                <div className="mt-6 flex items-baseline gap-1.5">
                  <span className="sc-heading text-3xl font-black">${p.amount.toFixed(2)}</span>
                  <span className="text-[10px] tracking-widest text-zinc-500">USD</span>
                </div>

                <button
                  data-testid={`credit-pack-${p.id}-buy`}
                  disabled={busy === p.id}
                  onClick={() => buy(p.id)}
                  className={`w-full mt-5 ${isBest ? "sc-btn sc-btn-volt" : "sc-btn"} flex items-center justify-center gap-2`}
                >
                  <Sparkles className="w-4 h-4" /> {busy === p.id ? "Redirecting..." : "Buy Gold Coins"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-14 border border-white/10 rounded-md p-6 sc-halftone">
        <div className="sc-label mb-3" style={{ color: "var(--sc-cyan)" }}>Two currencies · Two purposes</div>
        <div className="grid sm:grid-cols-2 gap-6 text-sm">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Coins className="w-4 h-4 text-[#FFD700]" />
              <div className="sc-heading text-base font-bold text-[#FFD700]">Gold Coins</div>
            </div>
            <p className="text-zinc-400 mt-1">Purchased with real money. Used to <span className="text-white">tip verified streamers</span> and <span className="text-white">buy subscriptions</span>. Redeemable to cash only by verified streamers (via Stripe payout).</p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-5 h-5" style={{ color: "var(--sc-cyan)" }} />
              <div className="sc-heading text-base font-bold text-[color:var(--sc-cyan)]">Sparks</div>
            </div>
            <p className="text-zinc-400 mt-1">Free play currency. Earned by watching streams, playing games actively, leveling up in communities, and as a <span className="text-white">bonus with every Gold Coin purchase (+100 per $10)</span>. Spent in the Bling Shop. <span className="text-white">Never redeemable.</span></p>
          </div>
        </div>
      </div>

      <div className="text-[11px] text-zinc-500 text-center mt-8 sc-mono">
        Gold Coins are entertainment tokens with no cash value unless paid out by a verified streamer via Stripe. Sparks have no cash value.
      </div>
    </div>
  );
}
